import React, { useState, useCallback, useEffect } from "react";
import {
  AlertCircleIcon,
  CameraIcon,
  EyeOffIcon,
  ImageIcon,
  ImageOffIcon,
  InfoIcon,
  MinusIcon,
  PlusIcon,
  RefreshCwIcon,
  XIcon,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import Cropper, { Area } from "react-easy-crop";
import heic2any from "heic2any";
import { Capacitor } from "@capacitor/core";
import Spinner from "../Spinner";
import {
  useCompletedUpload,
  useDeletePhoto,
  useGetUserPhotos,
  useUploadPhoto,
} from "@/tanstack-hooks";
import { useToast } from "@/hooks/use-toast";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { useTranslation } from "@/hooks/useTranslation";
import * as Sentry from "@sentry/react";
import { PhotoResponse } from "@/generated-api";
import SpinnerSmall from "../SpinnerSmall";
import { isHeicConversionError } from "@/lib/utils";
import { isSafari } from "react-device-detect";

type Photo = {
  url: string | null;
  failed: boolean;
};

const MIN_PHOTOS = 3;
const ASPECT_RATIO = 3 / 4;
const TARGET_WIDTH = 1080;
const TARGET_HEIGHT = 1440;
const MIN_WIDTH = 1080;
const MIN_HEIGHT = 1440;

type CroppedAreaPixels = {
  width: number;
  height: number;
  x: number;
  y: number;
} | null;

export default function PhotoUploadGrid({
  showContinue,
}: {
  showContinue?: boolean;
}) {
  const { toast } = useToast();
  const [photos, setPhotos] = useState<Photo[]>(
    Array.from({ length: 6 }, () => ({ url: null, failed: false })),
  );
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState<number | null>(
    null,
  );
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [maxZoom, setMaxZoom] = useState(3);
  const [croppedAreaPixels, setCroppedAreaPixels] =
    useState<CroppedAreaPixels>(null);
  const [currentImage, setCurrentImage] = useState<string | null>(null);
  const [lowResolutionAlert, setLowResolutionAlert] = useState(false);
  const [isReadingImage, setIsReadingImage] = useState(false);
  const [hasMinPhotos, setHasMinPhotos] = useState(false);
  const [loadingIndexes, setLoadingIndexes] = useState<number[]>([]);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  const userPhotosQuery = useGetUserPhotos();
  const uploadPhotoMutation = useUploadPhoto();
  const deletePhotoMutation = useDeletePhoto();
  const completedUploads = useCompletedUpload();
  const t = useTranslation();
  const isNative = Capacitor.isNativePlatform();

  useEffect(() => {
    if (userPhotosQuery.data) {
      const userPhotos = userPhotosQuery.data;
      // Initialize loading state for all photos
      setLoadingIndexes(Array.from({ length: userPhotos.length }, (_, i) => i));
      // Create an array of promises for loading each image
      const imageLoadPromises = userPhotos.map(
        (p: PhotoResponse, index) =>
          new Promise((resolve) => {
            const img = new Image();
            img.onload = () => {
              setLoadingIndexes((prev) => prev.filter((i) => i !== index));
              resolve({ index, url: p.medium_url, failed: false });
            };
            img.onerror = () => {
              setLoadingIndexes((prev) => prev.filter((i) => i !== index));
              Sentry.captureMessage("Image failed to load", {
                level: "error",
                extra: {
                  url: p.medium_url,
                  displayOrder: p.display_order,
                  photoType: p.photo_type,
                },
              });
              resolve({ index, url: p.medium_url, failed: true });
            };
            img.src = p.medium_url;
          }),
      );

      // Wait for all images to load
      Promise.allSettled(imageLoadPromises)
        .then((results) => {
          const loadedPhotos: Photo[] = Array.from({ length: 6 }, () => ({
            url: null,
            failed: false,
          }));

          for (const result of results) {
            if (result.status === "fulfilled") {
              const { index, url, failed } = result.value as {
                index: number;
                url: string | null;
                failed: boolean;
              };
              loadedPhotos[index] = {
                url,
                failed,
              };
            }
          }
          setPhotos(loadedPhotos);

          const publicPhotos = loadedPhotos.slice(0, MIN_PHOTOS);
          setHasMinPhotos(
            publicPhotos.length === MIN_PHOTOS &&
              publicPhotos.every(
                (photo) => photo.url !== null && !photo.failed,
              ),
          );
        })
        .catch((error) => {
          console.error("Error loading photos:", error);
        });
    }
  }, [userPhotosQuery.data]);

  const onCropComplete = useCallback((_: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const openCropForImage = useCallback(
    async (imageSrc: string, index: number) => {
      const img = await createImage(imageSrc);
      if (img.width < MIN_WIDTH || img.height < MIN_HEIGHT) {
        setLowResolutionAlert(true);
        return;
      }
      setCurrentImage(imageSrc);
      setCurrentPhotoIndex(index);
      const zoomFactorWidth = img.width / TARGET_WIDTH;
      const zoomFactorHeight = img.height / TARGET_HEIGHT;
      setMaxZoom(Math.min(zoomFactorWidth, zoomFactorHeight, 3));
      setCropModalOpen(true);
    },
    [],
  );

  const pickPhotoNative = useCallback(
    async (index: number) => {
      try {
        const { Camera, CameraResultType, CameraSource } =
          await import("@capacitor/camera");
        const photo = await Camera.getPhoto({
          source: CameraSource.Photos,
          resultType: CameraResultType.Uri,
          quality: 100,
          correctOrientation: true,
        });
        if (!photo.webPath) return;
        setIsReadingImage(true);
        await openCropForImage(photo.webPath, index);
      } catch (error) {
        // User cancellation throws — silently ignore
        if (error instanceof Error && /cancel/i.test(error.message)) return;
        console.error("Error picking photo:", error);
        Sentry.captureException(error);
        toast({
          variant: "destructive",
          title: t.PHOTO_UPLOAD_PAGE.ERRORS.UPLOAD,
          description: t.SHARED.ERRORS.PERSISTENT,
          className: "dark",
        });
      } finally {
        setIsReadingImage(false);
      }
    },
    [openCropForImage, toast, t],
  );

  const handleFileChange = useCallback(
    (index: number) => async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        setIsReadingImage(true);
        let imageBlob: Blob | File = file;

        const isHEIFContent = await checkHEIFContent(file);
        if (isHEIFContent) {
          setCropModalOpen(true);
          try {
            const convertedBlob = await heic2any({
              blob: file,
              toType: "image/jpeg",
              quality: 1,
            });
            imageBlob = convertedBlob as Blob;
          } catch (error) {
            console.error("Error converting HEIC/HEIF to JPEG:", error);
            if (
              isHeicConversionError(error) &&
              error.code === 1 &&
              error.message?.includes("already browser readable")
            ) {
              console.log(
                "File detected as HEIC but already readable, using original",
              );
              imageBlob = file;
            } else if (
              isHeicConversionError(error) &&
              error.code === 2 &&
              error.message?.includes("format not supported") &&
              isSafari
            ) {
              // Handle newer HEIC formats (macOS Sequoia and later)
              // that heic2any doesn't support but browsers can handle
              console.log(
                "HEIC format not supported by converter, attempting browser native handling",
              );
              imageBlob = file;
            } else {
              Sentry.captureException(error, {
                extra: {
                  fileName: file.name,
                  fileType: file.type,
                  fileSize: file.size,
                },
              });
              alert(t.PHOTO_UPLOAD_PAGE.ERRORS.HEIC_CONVERSION);
              setIsReadingImage(false);
              return;
            }
          }
        }

        const reader = new FileReader();
        reader.onload = async (e: ProgressEvent<FileReader>) => {
          const imageDataUrl = e.target?.result as string;
          await openCropForImage(imageDataUrl, index);
          setIsReadingImage(false);
        };
        reader.readAsDataURL(imageBlob);
      }
    },
    [],
  );

  async function checkHEIFContent(file: File): Promise<boolean> {
    try {
      // Read only the first 32 bytes of the file
      const buffer = await file.slice(0, 32).arrayBuffer();
      const uint8Array = new Uint8Array(buffer);
      // Check for 'ftyp' box
      if (getString(uint8Array, 4, 4) !== "ftyp") {
        return false;
      }
      // Check major brand
      const majorBrand = getString(uint8Array, 8, 4);
      // Check compatible brands (assuming at least one compatible brand)
      const compatibleBrand = getString(uint8Array, 16, 4);
      const heifBrands = [
        "heic",
        "heix",
        "hevc",
        "hevx",
        "heim",
        "heis",
        "hevm",
        "hevs",
        "mif1",
        "msf1",
      ];
      if (
        heifBrands.includes(majorBrand) ||
        heifBrands.includes(compatibleBrand)
      ) {
        return true;
      }
    } catch (error) {
      console.error("Error reading file buffer for HEIC detection:", error);
      // Fall through to filename/MIME type checks
    }

    if (
      file.type === "image/heic" ||
      file.name.toLowerCase().endsWith(".heic")
    ) {
      return true;
    }
    if (
      file.type === "image/heif" ||
      file.name.toLowerCase().endsWith(".heif")
    ) {
      return true;
    }

    return false;
  }

  function getString(data: Uint8Array, start: number, length: number): string {
    return String.fromCharCode.apply(
      null,
      Array.from(data.subarray(start, start + length)),
    );
  }

  const handleCropSave = useCallback(async () => {
    if (currentImage && croppedAreaPixels && currentPhotoIndex !== null) {
      const blob = await getCroppedImg(currentImage, croppedAreaPixels);
      // Set the current index as uploading
      setLoadingIndexes((prev) => [...prev, currentPhotoIndex]);
      setCropModalOpen(false);
      setZoom(1);

      if (!blob) {
        Sentry.captureException("Error in handleCropSave, no blob");
        toast({
          variant: "destructive",
          title: t.PHOTO_UPLOAD_PAGE.ERRORS.UPLOAD,
          description: t.SHARED.ERRORS.PERSISTENT,
          className: "dark",
        });
        return;
      }

      // Upload the photo
      uploadPhotoMutation.mutate(
        { photo: blob, display_order: currentPhotoIndex },
        {
          onSuccess: () => {
            void userPhotosQuery.refetch();
          },
          onError: (error) => {
            console.error("Error uploading photo:", error);
            Sentry.captureException(error);
            toast({
              variant: "destructive",
              title: t.PHOTO_UPLOAD_PAGE.ERRORS.UPLOAD,
              description: t.SHARED.ERRORS.PERSISTENT,
              className: "dark",
            });
          },
          onSettled: () => {
            setTimeout(() => {
              setLoadingIndexes((prev) =>
                prev.filter((index) => index !== currentPhotoIndex),
              );
            }, 1000);
          },
        },
      );
    }
  }, [
    currentImage,
    croppedAreaPixels,
    currentPhotoIndex,
    uploadPhotoMutation,
    toast,
    userPhotosQuery,
  ]);

  useEffect(() => {
    if (isFirstLoad && userPhotosQuery.isLoading) {
      setIsFirstLoad(false);
    }
  }, [isFirstLoad, userPhotosQuery.isLoading]);

  const handleRemovePhoto = (index: number) => {
    const newPhotos = [...photos];
    newPhotos[index] = { url: null, failed: false };
    setPhotos(newPhotos);

    deletePhotoMutation.mutate(index, {
      onError: (error) => {
        console.error("Error deleting photo:", error);
        Sentry.captureException(error);
        toast({
          variant: "destructive",
          title: t.PHOTO_UPLOAD_PAGE.ERRORS.DELETE,
          description: t.SHARED.ERRORS.PERSISTENT,
          className: "dark",
        });
      },
    });
  };

  function handleCompletedUploads() {
    completedUploads.mutate(
      {},
      {
        onError: (error) => {
          console.error("Error completing uploads:", error);
          Sentry.captureException(error);
          toast({
            variant: "destructive",
            title: t.PHOTO_UPLOAD_PAGE.ERRORS.UPLOADS,
            description: t.SHARED.ERRORS.PERSISTENT,
            className: "dark",
          });
        },
      },
    );
  }

  return (
    <>
      <Card
        className={`dark min-w-[320px] w-full ${
          showContinue ? "max-w-md rounded-3xl border border-zinc-600" : ""
        } mx-auto`}
      >
        <CardHeader>
          <div className="flex flex-row items-center justify-between">
            <CardTitle>{t.PHOTO_UPLOAD_PAGE.LABELS.CARD_TITLE}</CardTitle>
            <Popover>
              <PopoverTrigger asChild>
                <InfoIcon className="w-6 h-6 stroke-[#9D9D9D] shrink-0" />
              </PopoverTrigger>
              <PopoverContent className="w-80 dark">
                <div className="text-sm text-[#9D9D9D]">
                  {t.PHOTO_UPLOAD_PAGE.LABELS.MORE_INFO_FACE}
                </div>
                <div className="text-sm text-[#9D9D9D] mt-2">
                  {t.PHOTO_UPLOAD_PAGE.LABELS.MORE_INFO_FORBIDDEN}
                </div>
                <div className="text-sm text-[#9D9D9D] mt-2">
                  {t.PHOTO_UPLOAD_PAGE.LABELS.MORE_INFO_TIP}
                </div>
              </PopoverContent>
            </Popover>
          </div>
          <h2 className="mb-4 text-sm text-[#9D9D9D] max-w-md">
            {t.PHOTO_UPLOAD_PAGE.LABELS.DESCRIPTION}
          </h2>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            {photos.map((photo, index) => {
              // Check if the previous photo is filled (or if it's the first photo)
              const isDisabled = index > 0 && photos[index - 1].url === null;
              const isUploading = loadingIndexes.includes(index);

              return (
                <div
                  key={index}
                  className={`relative ${
                    index === 0 ? "col-span-2 row-span-2" : ""
                  }`}
                  style={{ aspectRatio: index === 0 ? "2 / 2" : "1 / 1" }}
                >
                  {!isNative && (
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange(index)}
                      className="hidden"
                      id={`photo-upload-${index}`}
                      disabled={isDisabled} // Disable input if the previous photo is not filled
                    />
                  )}
                  <label
                    htmlFor={isNative ? undefined : `photo-upload-${index}`}
                    onClick={
                      isNative && !isDisabled
                        ? () => void pickPhotoNative(index)
                        : undefined
                    }
                    className={`border-1 border border-[#4F4F4F] rounded-md absolute inset-0 flex items-center justify-center cursor-pointer overflow-hidden ${
                      isDisabled ? "cursor-not-allowed opacity-50" : ""
                    }`}
                  >
                    <div className="absolute inset-[2px] flex items-center justify-center rounded-sm overflow-hidden">
                      {photo.url && !photo.failed ? (
                        <img
                          src={photo.url}
                          alt={`Uploaded ${index}`}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="flex flex-col items-center justify-center p-4">
                          {photo.failed ? (
                            <ImageOffIcon
                              className="w-6 h-6 stroke-red-500"
                              strokeWidth={1.5}
                            />
                          ) : index < 3 ? (
                            <CameraIcon
                              className="w-6 h-6 stroke-[#4F4F4F]"
                              strokeWidth={1.5}
                            />
                          ) : (
                            <EyeOffIcon
                              className="w-6 stroke-[#4F4F4F] h-6"
                              strokeWidth={1.5}
                            />
                          )}
                        </div>
                      )}
                      {isUploading && (
                        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                          <Spinner />
                        </div>
                      )}
                    </div>
                  </label>
                  {!isDisabled && (
                    <div
                      className="absolute -bottom-2 -right-2 w-5 h-5 rounded-full bg-white flex items-center justify-center cursor-pointer"
                      onClick={(e) => {
                        e.preventDefault();
                        if (photo.url && index >= 3) {
                          handleRemovePhoto(index);
                        } else if (isNative) {
                          void pickPhotoNative(index);
                        } else {
                          document
                            .getElementById(`photo-upload-${index}`)
                            ?.click();
                        }
                      }}
                    >
                      {photo.url ? (
                        index >= 3 ? (
                          <MinusIcon className="h-4 w-4 text-black" />
                        ) : (
                          <RefreshCwIcon className="h-4 w-4 text-black" />
                        )
                      ) : (
                        <PlusIcon className="h-4 w-4 text-black" />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
        <CardFooter>
          {showContinue && (
            <Button
              type="submit"
              className="w-full"
              onClick={handleCompletedUploads}
              disabled={!hasMinPhotos || completedUploads.isPending}
            >
              {completedUploads.isPending ? (
                <SpinnerSmall black={true} />
              ) : (
                t.SHARED.LABELS.CONTINUE_BUTTON
              )}
            </Button>
          )}
        </CardFooter>
      </Card>

      {cropModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="rounded-2xl border border-zinc-700 bg-black w-full max-w-md md:max-w-lg lg:max-w-2xl h-[85vh] max-h-[85vh] flex flex-col overflow-y-auto">
            <div className="flex flex-col p-4">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-white">
                  {t.PHOTO_UPLOAD_PAGE.LABELS.CROP_MODAL_TILE}
                </h2>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setCropModalOpen(false)}
                  className="hover:bg-zinc-900"
                >
                  <XIcon className="h-6 w-6 text-white" />
                </Button>
              </div>
              <div className="flex flex-row items-center text-sm text-muted-foreground">
                <AlertCircleIcon className="h-4 w-4 mr-1 shrink-0" />
                {t.PHOTO_UPLOAD_PAGE.LABELS.CROP_MODAL_WARNING}
              </div>
            </div>

            <div className="relative h-[64vh]">
              {isReadingImage ? (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Spinner />
                </div>
              ) : (
                currentImage && (
                  <Cropper
                    image={currentImage}
                    crop={crop}
                    zoom={zoom}
                    aspect={ASPECT_RATIO}
                    onCropChange={setCrop}
                    onCropComplete={onCropComplete}
                    onZoomChange={setZoom}
                    maxZoom={maxZoom}
                    zoomSpeed={0.5}
                  />
                )
              )}
            </div>
            <div className="p-4 flex justify-end">
              <Button
                className="dark w-full sm:w-20"
                onClick={() => {
                  void handleCropSave();
                }}
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={lowResolutionAlert} onOpenChange={setLowResolutionAlert}>
        <DialogContent className="dark">
          <DialogTitle className="text-white flex flex-row items-center">
            <ImageIcon className="w-6 h-6" />
            <div className="ml-2">
              {t.PHOTO_UPLOAD_PAGE.ERRORS.LOW_RESOLUTION_TITLE}
            </div>
          </DialogTitle>
          <DialogDescription className="hidden" />
          <Alert variant="destructive" className="dark">
            <AlertDescription className="text-white">
              {t.PHOTO_UPLOAD_PAGE.ERRORS.LOW_RESOLUTION_DESCRIPTION(
                `${MIN_WIDTH}x${MIN_HEIGHT}`,
              )}
            </AlertDescription>
          </Alert>
          <DialogFooter>
            <Button
              className="w-full"
              onClick={() => setLowResolutionAlert(false)}
            >
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Helper function to create an image object
const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.src = url;
  });

// Helper function to crop and potentially upscale the image
const getCroppedImg = async (
  imageSrc: string,
  pixelCrop: { width: number; height: number; x: number; y: number },
): Promise<Blob | null> => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) return null;

  // Set canvas size to target dimensions
  canvas.width = TARGET_WIDTH;
  canvas.height = TARGET_HEIGHT;

  // Draw and potentially upscale cropped image
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    TARGET_WIDTH,
    TARGET_HEIGHT,
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.9);
  });
};
